import React, { useEffect, useRef } from 'react';
import { format } from 'date-fns';

// Contact person mapping for PO print
const PURCHASE_CONTACTS = {
  'Manish Gupta': '+91 8263845649',
  'Umesh Bhosale': '+91 9822242371',
  'Gopal Vyas': '+91 7498392817'
};

// Get contact info based on creator name
const getContactInfo = (createdByName) => {
  if (!createdByName) return { name: 'Purchase Team', phone: '' };
  
  // Try to find matching contact
  for (const [name, phone] of Object.entries(PURCHASE_CONTACTS)) {
    if (createdByName.toLowerCase().includes(name.toLowerCase().split(' ')[0])) {
      return { name, phone };
    }
  }
  
  // Default to first contact if no match
  const firstContact = Object.entries(PURCHASE_CONTACTS)[0];
  return { name: firstContact[0], phone: firstContact[1] };
};

const POPrint = ({ po, autoPrint = false, onPrintComplete }) => {
  const hasTriggeredPrint = useRef(false);

  useEffect(() => {
    if (autoPrint && po && !hasTriggeredPrint.current) {
      hasTriggeredPrint.current = true;
      setTimeout(() => {
        handlePrint();
      }, 500);
    }
  }, [autoPrint, po]);

  const handlePrint = () => {
    if (!po) return;

    const lineItems = po.line_items || [];
    
    // Get contact info from PO creator
    const contactInfo = getContactInfo(po.submitted_by_name || po.created_by_name);
    
    // Build line items table rows with updated column structure
    // Supplier item name now appears below item name in italics
    const lineItemRows = lineItems.map((item, idx) => `
      <tr>
        <td style="text-align: center; padding: 8px; border: 1px solid #ddd;">${idx + 1}</td>
        <td style="padding: 8px; border: 1px solid #ddd;">
          <div style="font-weight: 600;">${item.item_name}</div>
          ${item.supplier_item_name ? `<div style="font-style: italic; color: #555; font-size: 11px; margin-top: 2px;">${item.supplier_item_name}</div>` : ''}
        </td>
        <td style="text-align: right; padding: 8px; border: 1px solid #ddd;">${item.indent_quantity || item.quantity || 0}</td>
        <td style="text-align: center; padding: 8px; border: 1px solid #ddd;">${item.indent_unit || item.unit || '-'}</td>
        <td style="text-align: right; padding: 8px; border: 1px solid #ddd;">${item.quantity || 0}</td>
        <td style="text-align: center; padding: 8px; border: 1px solid #ddd;">${item.unit || '-'}</td>
        <td style="text-align: right; padding: 8px; border: 1px solid #ddd;">₹${(item.unit_price || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
        <td style="text-align: right; padding: 8px; border: 1px solid #ddd;">₹${(item.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
      </tr>
    `).join('');

    // Calculate totals
    const subTotal = po.sub_total || lineItems.reduce((sum, item) => sum + (item.amount || 0), 0);
    const otherChargesTaxable = po.other_charges_taxable || 0;
    const gstRate = po.gst_rate || 0;
    const gstAmount = po.gst_amount || 0;
    const otherChargesNonTaxable = po.other_charges_non_taxable || 0;
    const totalAmount = po.total_amount || subTotal;

    // Company logo URL
    const logoUrl = 'https://customer-assets.emergentagent.com/job_erp-bugfix-8/artifacts/isk2zb36_Logo%20final.png';

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Purchase Order - ${po.po_number}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: Arial, sans-serif; 
            font-size: 12px; 
            padding: 20px;
            max-width: 210mm;
            margin: 0 auto;
          }
          .header { 
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            border-bottom: 2px solid #333; 
            padding-bottom: 15px; 
            margin-bottom: 20px; 
          }
          .header-left {
            flex: 0 0 150px;
          }
          .header-left img {
            max-width: 140px;
            height: auto;
          }
          .header-right {
            flex: 1;
            text-align: right;
          }
          .header-right h1 { 
            font-size: 16px; 
            margin-bottom: 5px;
            color: #333;
          }
          .header-right p {
            margin: 2px 0;
            color: #555;
            font-size: 11px;
          }
          .header-right .contact-info {
            margin-top: 8px;
            font-size: 11px;
          }
          .po-title {
            text-align: center;
            margin-bottom: 15px;
          }
          .po-title h2 {
            font-size: 20px;
            color: #333;
            margin-bottom: 3px;
          }
          .po-title h3 {
            font-size: 14px;
            color: #666;
          }
          .po-info { 
            display: flex; 
            justify-content: space-between; 
            margin-bottom: 20px;
            gap: 20px;
          }
          .po-info-section { 
            flex: 1;
            padding: 12px;
            border: 1px solid #ddd;
            border-radius: 4px;
            background: #fafafa;
          }
          .po-info-section h3 {
            font-size: 12px;
            color: #333;
            margin-bottom: 8px;
            padding-bottom: 4px;
            border-bottom: 1px solid #ddd;
          }
          .po-info-section p { 
            margin: 3px 0; 
            color: #555;
            font-size: 11px;
          }
          .po-info-section strong {
            color: #333;
          }
          table { 
            width: 100%; 
            border-collapse: collapse; 
            margin-bottom: 20px;
          }
          th { 
            background: #f0f0f0; 
            padding: 8px 6px; 
            border: 1px solid #ddd; 
            text-align: left;
            font-weight: bold;
            color: #333;
            font-size: 11px;
          }
          td { 
            padding: 6px; 
            border: 1px solid #ddd;
            vertical-align: top;
            font-size: 11px;
          }
          .totals-section {
            display: flex;
            justify-content: flex-end;
            margin-top: 20px;
          }
          .totals-table {
            width: 300px;
          }
          .totals-table td {
            padding: 6px 10px;
            font-size: 11px;
          }
          .totals-table .label {
            text-align: right;
            color: #555;
          }
          .totals-table .value {
            text-align: right;
            font-weight: bold;
            color: #333;
          }
          .totals-table .grand-total {
            background: #f0f0f0;
            font-size: 12px;
          }
          .footer {
            margin-top: 40px;
            display: flex;
            justify-content: space-between;
          }
          .signature-box {
            width: 180px;
            text-align: center;
          }
          .signature-line {
            border-top: 1px solid #333;
            margin-top: 50px;
            padding-top: 5px;
            font-size: 11px;
          }
          .signature-name {
            font-size: 10px;
            color: #555;
            margin-top: 3px;
          }
          .terms {
            margin-top: 25px;
            padding: 12px;
            background: #fafafa;
            border: 1px solid #ddd;
            border-radius: 4px;
          }
          .terms h4 {
            margin-bottom: 8px;
            color: #333;
            font-size: 12px;
          }
          .terms ol {
            margin-left: 18px;
            color: #555;
            font-size: 11px;
          }
          .terms li {
            margin: 4px 0;
          }
          @media print {
            body { padding: 10px; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="header-left">
            <img src="${logoUrl}" alt="Dreamspan Logo" />
          </div>
          <div class="header-right">
            <h1>DREAMSPAN VENTURES PRIVATE LIMITED</h1>
            <p>556/1/2, Balaji Hotel Lane, Pisoli</p>
            <p>Katraj Kondhwa Road, Pune-411060</p>
            <p>GSTIN: 27AAHCD8512E1ZR</p>
            <div class="contact-info">
              <p><strong>Contact Person:</strong> ${contactInfo.name}${contactInfo.phone ? `, ${contactInfo.phone}` : ''}</p>
            </div>
          </div>
        </div>

        <div class="po-title">
          <h2>PURCHASE ORDER</h2>
          <h3>${po.po_number}</h3>
        </div>

        <div class="po-info">
          <div class="po-info-section">
            <h3>Supplier Details</h3>
            <p><strong>${po.supplier_name}</strong></p>
          </div>
          <div class="po-info-section">
            <h3>Ship To (${po.shipping_unit || 'Unit 1'})</h3>
            ${po.shipping_address ? `
              <p><strong>${po.shipping_address.company}</strong></p>
              <p>${po.shipping_address.address_line1}</p>
              <p>${po.shipping_address.address_line2}</p>
              <p>${po.shipping_address.city}, ${po.shipping_address.state} - ${po.shipping_address.pin_code}</p>
            ` : `
              <p><strong>Dreamspan Ventures Pvt Ltd</strong></p>
              <p>S. No. 37, Plot No 556/1/2, Near Balaji Hotel</p>
              <p>Pisoli Village, Taluka Haweli</p>
              <p>Pune, Maharashtra - 411060</p>
            `}
          </div>
          <div class="po-info-section">
            <h3>PO Details</h3>
            <p><strong>PO Date:</strong> ${format(new Date(po.po_date), 'dd/MM/yyyy')}</p>
            <p><strong>Expected Delivery:</strong> ${po.expected_delivery_date ? format(new Date(po.expected_delivery_date), 'dd/MM/yyyy') : 'Not specified'}</p>
            <p><strong>Status:</strong> ${po.status?.toUpperCase()}</p>
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th style="width: 35px; text-align: center;">S.No</th>
              <th style="min-width: 150px;">Item</th>
              <th style="width: 60px; text-align: right;">Qty</th>
              <th style="width: 55px; text-align: center;">UOM</th>
              <th style="width: 70px; text-align: right;">Supplier Qty</th>
              <th style="width: 65px; text-align: center;">Supplier UOM</th>
              <th style="width: 70px; text-align: right;">Rate</th>
              <th style="width: 80px; text-align: right;">Amount</th>
            </tr>
          </thead>
          <tbody>
            ${lineItemRows}
          </tbody>
        </table>

        <div class="totals-section">
          <table class="totals-table">
            <tr>
              <td class="label">Sub Total:</td>
              <td class="value">₹${subTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
            </tr>
            ${otherChargesTaxable > 0 ? `
            <tr>
              <td class="label">Other Charges (Taxable):</td>
              <td class="value">₹${otherChargesTaxable.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
            </tr>
            ` : ''}
            <tr>
              <td class="label">GST (${gstRate}%):</td>
              <td class="value">₹${gstAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
            </tr>
            ${otherChargesNonTaxable > 0 ? `
            <tr>
              <td class="label">Other Charges:</td>
              <td class="value">₹${otherChargesNonTaxable.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
            </tr>
            ` : ''}
            <tr class="grand-total">
              <td class="label">Grand Total:</td>
              <td class="value">₹${totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
            </tr>
          </table>
        </div>

        ${po.notes ? `
        <div class="terms">
          <h4>Notes:</h4>
          <p>${po.notes}</p>
        </div>
        ` : ''}

        <div class="terms">
          <h4>Terms & Conditions:</h4>
          <ol>
            <li>Please quote PO number on all invoices and delivery challans.</li>
            <li>Material should be delivered as per the specifications mentioned.</li>
            <li>Payment terms as per our agreement.</li>
            <li>Any deviation in quantity or quality should be intimated prior to dispatch.</li>
          </ol>
        </div>

        <div class="footer">
          <div class="signature-box">
            <div class="signature-line">Prepared By</div>
            ${po.submitted_by_name ? `<div class="signature-name">${po.submitted_by_name}</div>` : ''}
          </div>
          <div class="signature-box">
            <div class="signature-line">Approved By</div>
            ${po.approved_by_name ? `<div class="signature-name">${po.approved_by_name}</div>` : ''}
          </div>
          <div class="signature-box">
            <div class="signature-line">Supplier Signature</div>
          </div>
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        if (onPrintComplete) {
          onPrintComplete();
        }
      }, 250);
    }
  };

  return null; // This component doesn't render anything visible
};

export default POPrint;
